import { request } from './client';
import type {
  AbdmApiCallLog,
  AbdmEventLog,
  AbdmMetricsDaily,
  AbdmTransactionLog,
  ApiMaster,
  Facility,
  Page,
  RollupResult,
  Tenant,
  TenantApiAccess,
  TenantSecurityPolicy,
  TraceTimeline,
  WebhookOutbox,
  WebhookOutboxStatus,
  WebhookSubscription,
} from '@/types/backend';

/**
 * Platform Owner API surface — every call hits an `/admin/*` endpoint
 * gated by `@RequirePlatformAdmin` server-side. We don't add a tenantId
 * header here on purpose: these endpoints are cross-tenant by design,
 * and server-side authorization happens via the JWT role claim.
 *
 * Functions return the parsed body. Errors propagate as axios errors
 * from the shared client; SWR + the page-level error UI handle them.
 */

// ---------------- API Master (catalog) ----------------

/**
 * Catalog of every API exposed by the connector. Backed by
 * {@code /api/abdm/admin/api-master} — single source of truth that
 * drives per-tenant access overrides (TenantApiAccess).
 */
export const apiMaster = {
  list: () =>
    request<ApiMaster[]>({ url: '/api/abdm/admin/api-master', method: 'GET' }),
  listByMilestone: (milestone: string) =>
    request<ApiMaster[]>({
      url: `/api/abdm/admin/api-master/milestone/${encodeURIComponent(milestone)}`,
      method: 'GET',
    }),
  tenantAccess: (tenantId: string) =>
    request<TenantApiAccess[]>({
      url: `/api/abdm/admin/tenants/${encodeURIComponent(tenantId)}/api-access`,
      method: 'GET',
    }),
  setTenantAccess: (tenantId: string, body: { apiCode: string; access: 'ALLOW' | 'DENY'; reason?: string; rateLimitPerMinute?: number }) =>
    request<TenantApiAccess>({
      url: `/api/abdm/admin/tenants/${encodeURIComponent(tenantId)}/api-access`,
      method: 'PUT',
      data: body,
    }),
  deleteTenantAccess: (tenantId: string, apiCode: string) =>
    request<void>({
      url: `/api/abdm/admin/tenants/${encodeURIComponent(tenantId)}/api-access/${encodeURIComponent(apiCode)}`,
      method: 'DELETE',
    }),
};

// ---------------- Tenants (master) ----------------

export interface TenantCreateRequest {
  tenantId?: string;
  organizationName: string;
  organizationType?: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  hfrId?: string;
  callbackUrl?: string;
  abhaEnabled?: boolean;
  status?: string;
  /** Tenant-admin login material. Persisted on the Tenant doc (legacy schema). */
  username?: string;
  password?: string;
  /** Defaults to TENANT_ADMIN server-side when blank. */
  role?: string;
  /** Tenant-admin user details — distinct from tenant-level contact fields. */
  userFullName?: string;
  userEmail?: string;
  userMobile?: string;
  /** Address — populates the embedded Tenant.contact.address. */
  address?: string;
  // ABDM identity (per the integration document). Without these the
  // connector can't sign M2/M3 calls as the right tenant.
  abdmHIPID?: string;
  abdmXCMID?: string;
  abdmXHIUID?: string;
  clientId?: string;
  clientSecret?: string;
  credentialProvider?: boolean;
  credentialProviderId?: string;
  // Patient datasource (M2 link flow against tenant's own EHR). Most
  // tenants leave these blank and use the default Mongo store.
  databaseType?: string;
  databaseUrl?: string;
  dbUsername?: string;
  dbPassword?: string;
  databaseName?: string;
  patientTableName?: string;
  patientCollectionName?: string;
  maxPoolSize?: number;
  connectionTimeout?: number;
  // FRD additions
  parentTenantId?: string;
  gstin?: string;
  environment?: 'SANDBOX' | 'PRODUCTION' | string;
  plan?: 'STARTER' | 'PROVIDER' | 'NETWORK' | 'ENTERPRISE' | string;
  integrationMode?: 'PLATFORM' | 'ENTERPRISE' | 'SDK';
  /** @deprecated use integrationMode. */
  directBridge?: boolean;
}

export interface TenantUpdateRequest extends Partial<TenantCreateRequest> {
  status?: string;
}

export interface AbdmCredentialsBody {
  clientId: string;
  /** Blank → keep existing. */
  clientSecret?: string;
  abdmHIPID: string;
  abdmXCMID?: string;
  abdmXHIUID?: string;
}

export interface AbdmProbeResult {
  ok: boolean;
  durationMs?: number;
  hipId?: string;
  integrationMode?: string;
  message?: string;
  tokenIssued?: boolean;
  error?: string;
}

export interface BridgeCredentialRow {
  id: string;
  environment: 'SANDBOX' | 'PRODUCTION';
  purpose: 'ABDM_GATEWAY' | 'ABHA_SERVICE' | 'LEGACY_UNIFIED';
  clientId: string;
  callbackUrl?: string | null;
  active: boolean;
  lastProbeAt?: string | null;
  lastProbeOk?: boolean | null;
  createdAt?: string;
}

export interface SplitCredentialPair {
  purpose: 'ABDM_GATEWAY' | 'ABHA_SERVICE' | 'LEGACY_UNIFIED';
  clientId: string;
  clientSecret: string;
  callbackUrl?: string;
}

export interface SplitCredentialsRequest {
  environment: 'SANDBOX' | 'PRODUCTION';
  credentials: SplitCredentialPair[];
}

export const tenants = {
  list:         () =>
    request<Tenant[]>({ url: '/admin/tenants', method: 'GET' }),
  recent:       (limit = 10) =>
    request<Tenant[]>({ url: '/admin/tenants/recent', method: 'GET', params: { limit } }),
  get:          (tenantId: string) =>
    request<Tenant>({ url: `/admin/tenants/${encodeURIComponent(tenantId)}`, method: 'GET' }),
  create:       (body: TenantCreateRequest) =>
    request<Tenant>({ url: '/admin/tenants', method: 'POST', data: body }),
  update:       (tenantId: string, body: TenantUpdateRequest) =>
    request<Tenant>({ url: `/admin/tenants/${encodeURIComponent(tenantId)}`, method: 'PATCH', data: body }),
  changeStatus: (tenantId: string, status: string) =>
    request<Tenant>({ url: `/admin/tenants/${encodeURIComponent(tenantId)}/status`, method: 'PATCH', data: { status } }),
  /** ENTERPRISE-mode credential intake. Upserts the linked credential_provider. */
  upsertAbdmCredentials: (tenantId: string, body: AbdmCredentialsBody) =>
    request<Tenant>({
      url: `/admin/tenants/${encodeURIComponent(tenantId)}/abdm-credentials`,
      method: 'PUT',
      data: body,
    }),
  /** §29 split-credentials intake (ENTERPRISE / SDK). */
  upsertSplitCredentials: (tenantId: string, body: SplitCredentialsRequest) =>
    request<{ environment: string; activeCount: number }>({
      url: `/admin/tenants/${encodeURIComponent(tenantId)}/abdm-credentials/split`,
      method: 'PUT',
      data: body,
    }),
  listSplitCredentials: (tenantId: string) =>
    request<BridgeCredentialRow[]>({
      url: `/admin/tenants/${encodeURIComponent(tenantId)}/abdm-credentials/split`,
      method: 'GET',
    }),
  promoteToProduction: (tenantId: string) =>
    request<{ tenantId: string; currentEnv: string; bridgeState: string }>({
      url: `/admin/tenants/${encodeURIComponent(tenantId)}/promote-to-production`,
      method: 'POST',
    }),
  /** Connectivity probe — calls /sessions with the resolved creds. */
  probe: (tenantId: string) =>
    request<AbdmProbeResult>({
      url: `/admin/tenants/${encodeURIComponent(tenantId)}/probe`,
      method: 'POST',
    }),
};

// ---------------- Facilities (PR-6) ----------------

export const facilities = {
  list:        (params?: { tenantId?: string; hipId?: string }) =>
    request<Facility[]>({ url: '/admin/abdm/facilities', method: 'GET', params }),
  get:         (tenantId: string, facilityId: string) =>
    request<Facility>({ url: `/admin/abdm/facilities/${encodeURIComponent(tenantId)}/${encodeURIComponent(facilityId)}`, method: 'GET' }),
};

// Tenant-scoped CRUD lives in /api/abdm/facilities/* and runs under
// the calling user's tenant context. Platform admins reading another
// tenant's row use `facilities.get(tenantId, facilityId)` above.
export const facilitiesScoped = {
  list:        (activeOnly = false) =>
    request<Facility[]>({ url: '/api/abdm/facilities', method: 'GET', params: { activeOnly } }),
  create:      (body: Partial<Facility>) =>
    request<Facility>({ url: '/api/abdm/facilities', method: 'POST', data: body }),
  update:      (facilityId: string, body: Partial<Facility>) =>
    request<Facility>({ url: `/api/abdm/facilities/${encodeURIComponent(facilityId)}`, method: 'PUT', data: body }),
  deactivate:  (facilityId: string) =>
    request<Facility>({ url: `/api/abdm/facilities/${encodeURIComponent(facilityId)}`, method: 'DELETE' }),
};

// ---------------- Tenant Security (PR-5) ----------------

export interface SecurityPolicyUpdate {
  ipAllowlist?: string[];
  rpmLimit: number;
  tokenTtlSeconds: number;
  enforce: boolean;
  description?: string;
}

export const tenantSecurity = {
  list:    () =>
    request<TenantSecurityPolicy[]>({ url: '/admin/tenant-security/policies', method: 'GET' }),
  get:     (tenantId: string) =>
    request<TenantSecurityPolicy>({ url: `/admin/tenant-security/policies/${encodeURIComponent(tenantId)}`, method: 'GET' }),
  upsert:  (tenantId: string, body: SecurityPolicyUpdate) =>
    request<TenantSecurityPolicy>({ url: `/admin/tenant-security/policies/${encodeURIComponent(tenantId)}`, method: 'PUT', data: body }),
  remove:  (tenantId: string) =>
    request<void>({ url: `/admin/tenant-security/policies/${encodeURIComponent(tenantId)}`, method: 'DELETE' }),
};

// ---------------- Audit Viewer (PR-4) ----------------

export interface AuditEventQuery {
  tenantId?: string;
  milestone?: string;
  status?: string;
  eventType?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
}

export interface AuditTransactionQuery {
  tenantId?: string;
  status?: string;
  apiName?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
}

export interface AdminApiCallLogsQuery {
  tenantId?: string;
  abdmStatus?: 'OK' | 'ERROR' | 'SKIPPED' | 'N/A';
  page?: number;
  size?: number;
}

export const audit = {
  events:        (q: AuditEventQuery) =>
    request<Page<AbdmEventLog>>({ url: '/admin/abdm/audit/events', method: 'GET', params: q }),
  transactions:  (q: AuditTransactionQuery) =>
    request<Page<AbdmTransactionLog>>({ url: '/admin/abdm/audit/transactions', method: 'GET', params: q }),
  trace:         (traceId: string, tenantId?: string) =>
    request<TraceTimeline>({
      url: `/admin/abdm/audit/trace/${encodeURIComponent(traceId)}`,
      method: 'GET',
      params: tenantId ? { tenantId } : undefined,
    }),
  apiCallLogs:   (q: AdminApiCallLogsQuery) =>
    request<Page<AbdmApiCallLog>>({ url: '/admin/abdm/audit/api-call-logs', method: 'GET', params: q }),
};

// ---------------- Webhooks (PR-3) ----------------

export interface DeliveryListQuery {
  tenantId?: string;
  status?: WebhookOutboxStatus;
  page?: number;
  size?: number;
}

export interface SubscriptionUpsertBody {
  tenantId?: string;
  url?: string;
  secret?: string;
  secretId?: string;
  eventTypes?: string[];
  active?: boolean;
  description?: string;
}

export const webhooks = {
  deliveries:    (q: DeliveryListQuery) =>
    request<Page<WebhookOutbox>>({ url: '/admin/webhooks/deliveries', method: 'GET', params: q }),
  delivery:      (id: string) =>
    request<WebhookOutbox>({ url: `/admin/webhooks/deliveries/${encodeURIComponent(id)}`, method: 'GET' }),
  replay:        (id: string) =>
    request<{ id: string; status: string; nextRetryAt: string; maxAttempts: number }>({
      url: `/admin/webhooks/deliveries/${encodeURIComponent(id)}/replay`,
      method: 'POST',
    }),
  subscriptions: (params?: { tenantId?: string }) =>
    request<WebhookSubscription[]>({ url: '/admin/webhooks/subscriptions', method: 'GET', params }),
  createSubscription: (body: SubscriptionUpsertBody) =>
    request<WebhookSubscription>({ url: '/admin/webhooks/subscriptions', method: 'POST', data: body }),
  updateSubscription: (id: string, body: SubscriptionUpsertBody) =>
    request<WebhookSubscription>({ url: `/admin/webhooks/subscriptions/${encodeURIComponent(id)}`, method: 'PATCH', data: body }),
  deleteSubscription: (id: string) =>
    request<void>({ url: `/admin/webhooks/subscriptions/${encodeURIComponent(id)}`, method: 'DELETE' }),
};

// ---------------- Metrics Rollup (PR-8) ----------------

export const rollup = {
  run:       (date?: string) =>
    request<RollupResult>({
      url: '/admin/abdm/metrics-rollup/run',
      method: 'POST',
      params: date ? { date } : undefined,
    }),
  backfill:  (from: string, to: string) =>
    request<Record<string, RollupResult>>({
      url: '/admin/abdm/metrics-rollup/backfill',
      method: 'POST',
      params: { from, to },
    }),
  read:      (tenantId: string, from: string, to: string) =>
    request<AbdmMetricsDaily[]>({
      url: '/admin/abdm/metrics-rollup',
      method: 'GET',
      params: { tenantId, from, to },
    }),
};

// ---------------- Secret status (PR-7) ----------------
//
// PR-7 didn't expose a dedicated "what's the cipher status?" endpoint;
// the visible signal is the WARN log at boot when the plaintext fallback
// is active. The Secrets page surfaces what we CAN see — webhook
// subscriptions' kekId distribution from the existing subscriptions
// listing — and explains that fuller visibility is a follow-up. This
// keeps PR-B honest: we don't fabricate an endpoint we haven't built.
